import type { FormikValues } from 'formik';
import React from 'react';
import type { FormProps } from '../../components/Form';
import FormModal from '../../components/FormModal';

export type OrganizationOnboardingStepBaseProps = {
  onPrevious: (() => void) | null;
  onNext: () => void;
  lastStep: boolean;
};

interface OrganizationOnboardingStepProps<Values extends FormikValues>
  extends Omit<FormProps<Values>, 'onSubmit'>,
    OrganizationOnboardingStepBaseProps {
  children: React.ReactNode;
  title: string;
  description?: string;
  footer?: (values: Values) => React.ComponentProps<typeof FormModal>['footer'];
  onSubmit: (values: Values) => Promise<boolean | void>;
  disabled?: boolean;
}

const OrganizationOnboardingStep = <Values extends FormikValues>({
  children,
  title,
  description,
  footer,
  onSubmit,
  onPrevious,
  onNext,
  lastStep,
  disabled,
  ...props
}: OrganizationOnboardingStepProps<Values>) => {
  return (
    <FormModal<Values>
      title={title}
      description={description}
      width={540}
      variant="expanded"
      form={{
        ...props,
        onSubmit: async (values) =>
          disabled
            ? Promise.reject(new Error('Form is disabled'))
            : onSubmit(values).then((ok = true) => {
                if (ok) {
                  onNext();
                }
              }),
      }}
      footer={({ isValid, values }) => ({
        submitButton: {
          text: lastStep ? 'Finish' : 'Next',
          disabled: !isValid || disabled,
        },
        leftButton: onPrevious
          ? {
              text: 'Previous',
              onClick: onPrevious,
            }
          : undefined,
        ...footer?.(values),
      })}
    >
      {children}
    </FormModal>
  );
};

export default OrganizationOnboardingStep;
